import { RegionShared } from "@1games-ru/region-shared";
import {
  BuyMaterial,
  GasStationDto,
  ShopDto,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { BusinessType } from "@1games-ru/region-shared/dist/enums/business/business";
import { DeliveryType } from "@1games-ru/region-shared/dist/enums/business/delivery";
import { EmployeeRole } from "@1games-ru/region-shared/dist/enums/business/employee";
import { JobType } from "@1games-ru/region-shared/dist/enums/business/service";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { Border, Point } from "@1games-ru/region-shared/dist/types/world/world";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider.ts";

export default class BusinessMpProvider {
  // Информация о бизнесе
  public static async getInfo(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.GetBusinessInfoResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessInfoRequest,
      typeof RegionShared.Backend.Responses.GetBusinessInfoResponse
    >(RegionShared.Backend.Events.Business.GetBusinessInfo, { businessId });
  }

  // Получить список бизнесов пользователя
  public static async getMyBusinesses(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.GetMyBusinessesResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetMyBusinessesRequest,
      typeof RegionShared.Backend.Responses.GetMyBusinessesResponse
    >(RegionShared.Backend.Events.Business.GetBusinesses, {});
  }

  // Создать бизнес
  public static async createBusiness(
    type: BusinessType,
    dimension: number,
    shops: ShopDto[],
    gasStations: GasStationDto[],
    office: Point,
    jobs: Point[],
    border: Border,
    sign: Point,
    price: number,
    characterId: number | null,
    streetName: string,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessCreateResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessCreateRequest,
      typeof RegionShared.Backend.Responses.BusinessCreateResponse
    >(RegionShared.Backend.Events.Business.BusinessCreate, {
      type,
      dimension,
      shops,
      gasStations,
      office,
      jobs,
      border,
      sign,
      price,
      characterId,
      streetName,
    });
  }

  // Купить бизнес
  public static async buyBusiness(
    businessId: number,
    paymentType: PaymentType,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessBuyResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessBuyRequest,
      typeof RegionShared.Backend.Responses.BusinessBuyResponse
    >(RegionShared.Backend.Events.Business.BusinessBuy, { businessId, paymentType });
  }

  // Продать бизнес государству
  public static async sellBusiness(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessSellResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessSellRequest,
      typeof RegionShared.Backend.Responses.BusinessSellResponse
    >(RegionShared.Backend.Events.Business.BusinessSell, { businessId });
  }

  // Продать бизнес другому игроку
  public static async tradeBusiness(
    businessId: number,
    characterId: number,
    price: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessTradeResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessTradeRequest,
      typeof RegionShared.Backend.Responses.BusinessTradeResponse
    >(RegionShared.Backend.Events.Business.BusinessTrade, {
      businessId,
      characterId,
      price,
    });
  }

  // Добавить сотрудника
  public static async addEmployee(
    businessId: number,
    characterId: number,
    role: EmployeeRole,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessEmployeeAddResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessEmployeeAddRequest,
      typeof RegionShared.Backend.Responses.BusinessEmployeeAddResponse
    >(RegionShared.Backend.Events.Business.BusinessEmployeeAdd, {
      businessId,
      characterId,
      role,
    });
  }

  // Изменить роль сотрудника
  public static async updateEmployee(
    businessId: number,
    characterId: number,
    role: EmployeeRole,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessEmployeeUpdateResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessEmployeeUpdateRequest,
      typeof RegionShared.Backend.Responses.BusinessEmployeeUpdateResponse
    >(RegionShared.Backend.Events.Business.BusinessEmployeeUpdate, {
      businessId,
      characterId,
      role,
    });
  }

  // Удалить сотрудника
  public static async removeEmployee(
    businessId: number,
    characterId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessEmployeeRemoveResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessEmployeeRemoveRequest,
      typeof RegionShared.Backend.Responses.BusinessEmployeeRemoveResponse
    >(RegionShared.Backend.Events.Business.BusinessEmployeeRemove, {
      businessId,
      characterId,
    });
  }

  // Уволиться из бизнеса самостоятельно
  public static async removeSelfFromBusiness(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessEmployeeSelfRemoveResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessEmployeeSelfRemoveRequest,
      typeof RegionShared.Backend.Responses.BusinessEmployeeSelfRemoveResponse
    >(RegionShared.Backend.Events.Business.BusinessEmployeeSelfRemove, {
      businessId,
    });
  }

  // Получить цену продажи бизнеса
  public static async getSellPrice(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessSellPriceResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessSellPriceRequest,
      typeof RegionShared.Backend.Responses.GetBusinessSellPriceResponse
    >(RegionShared.Backend.Events.Business.GetBusinessSellPrice, {
      businessId,
    });
  }

  // Оплатить налог
  public static async payBusinessFee(
    businessId: number,
    amount: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessFeePayResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessFeePayRequest,
      typeof RegionShared.Backend.Responses.BusinessFeePayResponse
    >(RegionShared.Backend.Events.Business.BusinessFeePay, {
      businessId,
      amount,
    });
  }

  // Купить улучшения
  public static async improveBusiness(
    businessId: number,
    improvementId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessImproveResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessImproveRequest,
      typeof RegionShared.Backend.Responses.BusinessImproveResponse
    >(RegionShared.Backend.Events.Business.BusinessImprove, {
      businessId,
      improvementId,
    });
  }

  // Получить информацию об улучшениях
  public static async getImprovements(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessImprovementsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessImprovementsRequest,
      typeof RegionShared.Backend.Responses.GetBusinessImprovementsResponse
    >(RegionShared.Backend.Events.Business.GetBusinessImprovements, {
      businessId,
    });
  }

  // Начать работу (оба типа)
  public static async startBusinessJob(
    businessId: number,
    jobType: JobType,
    isImmediately: boolean,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.BusinessJobStartResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessJobStartRequest,
      typeof RegionShared.Backend.Responses.BusinessJobStartResponse
    >(RegionShared.Backend.Events.Business.BusinessJobStart, {
      businessId,
      jobType,
      isImmediately,
    });
  }

  // Действие при старте работы (не надо)
  public static async startBusinessJobAction(
    jobType: JobType,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessJobStartActionResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessJobStartActionRequest,
      typeof RegionShared.Backend.Responses.BusinessJobStartActionResponse
    >(RegionShared.Backend.Events.Business.BusinessJobStartAction, {
      jobType,
    });
  }

  // Действие при окончании работы (не надо)
  public static async stopBusinessJobAction(
    jobType: JobType,
    top: number,
    torso: number,
    pants: number,
    shoes: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessJobStopActionResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessJobStopActionRequest,
      typeof RegionShared.Backend.Responses.BusinessJobStopActionResponse
    >(RegionShared.Backend.Events.Business.BusinessJobStopAction, {
      jobType,
      top,
      torso,
      pants,
      shoes,
    });
  }

  // Завершить ручную работу (не надо)
  public static async manualJobFinish(
    businessId: number,
    jobType: JobType,
    isImmediately: boolean,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessManualJobFinishResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessManualJobFinishRequest,
      typeof RegionShared.Backend.Responses.BusinessManualJobFinishResponse
    >(RegionShared.Backend.Events.Business.BusinessManualJobFinish, {
      businessId,
      jobType,
      isImmediately,
    });
  }

  // Получить список сотрудников
  public static async getEmployees(
    businessId: number,
    roles: EmployeeRole[],
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessEmployeesResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessEmployeesRequest,
      typeof RegionShared.Backend.Responses.GetBusinessEmployeesResponse
    >(RegionShared.Backend.Events.Business.GetBusinessEmployees, {
      businessId,
      role: roles,
    });
  }

  // Получить информацию о налоге по бизнесу (состояние платежей)
  public static async getFee(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.GetBusinessFeeResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessFeeRequest,
      typeof RegionShared.Backend.Responses.GetBusinessFeeResponse
    >(RegionShared.Backend.Events.Business.GetBusinessFee, {
      businessId,
    });
  }

  // Получить баланс бизнеса (сейф)
  public static async getBalance(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.GetBusinessBalanceResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessBalanceRequest,
      typeof RegionShared.Backend.Responses.GetBusinessBalanceResponse
    >(RegionShared.Backend.Events.Business.GetBusinessBalance, {
      businessId,
    });
  }

  // Вывод денег из сейфа
  public static async takeFromSafe(
    businessId: number,
    amount: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessTakeFromSafeResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessTakeFromSafeRequest,
      typeof RegionShared.Backend.Responses.BusinessTakeFromSafeResponse
    >(RegionShared.Backend.Events.Business.BusinessTakeFromSafe, {
      businessId,
      amount,
    });
  }

  // Получить состояние сервиса бизнеса (чистота, ремонт и цены)
  public static async getServiceState(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessServiceStateResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessServiceStateRequest,
      typeof RegionShared.Backend.Responses.GetBusinessServiceStateResponse
    >(RegionShared.Backend.Events.Business.GetBusinessServiceState, {
      businessId,
    });
  }

  // Цены на услуги - получить список
  public static async getServiceProductsList(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessProductsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessProductsRequest,
      typeof RegionShared.Backend.Responses.GetBusinessProductsResponse
    >(RegionShared.Backend.Events.Business.GetBusinessProducts, {
      businessId,
    });
  }

  // Цены на услуги - установить наценку
  public static async setServiceProductExtraCharge(
    businessId: number,
    productId: number,
    percent: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.SetBusinessProductPercentResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.SetBusinessProductPercentRequest,
      typeof RegionShared.Backend.Responses.SetBusinessProductPercentResponse
    >(RegionShared.Backend.Events.Business.SetBusinessProductPercent, {
      businessId,
      productId,
      percent,
    });
  }

  // Товары и склад - получить список товаров
  public static async getWarehouseGoodsList(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessMaterialsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessMaterialsRequest,
      typeof RegionShared.Backend.Responses.GetBusinessMaterialsResponse
    >(RegionShared.Backend.Events.Business.GetBusinessMaterials, {
      businessId,
    });
  }

  // Товары и склад - оплатить товары
  public static async payWarehouseGoodsList(
    businessId: number,
    characterId: number,
    deliveryType: DeliveryType,
    materials: BuyMaterial[],
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessBuyMaterialsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessBuyMaterialsRequest,
      typeof RegionShared.Backend.Responses.BusinessBuyMaterialsResponse
    >(RegionShared.Backend.Events.Business.BusinessBuyMaterials, {
      businessId,
      characterId,
      deliveryType,
      materials,
    });
  }

  // закрыть ПК
  public static async hidePc(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Cef.Responses.BusinessHideOfficePcResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Cef.Requests.BusinessHideOfficePcRequest,
      typeof RegionShared.Cef.Responses.BusinessHideOfficePcResponse
    >(RegionShared.Cef.Events.Business.BusinessOfficePcHide, {});
  }

  // Пополнить баланс бизнеса
  public static async topUpBusinessBalance(
    businessId: number,
    amount: number,
    paymentType: PaymentType,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.BusinessExpenseBalanceDepositResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.BusinessExpenseBalanceDepositRequest,
      typeof RegionShared.Backend.Responses.BusinessExpenseBalanceDepositResponse
    >(RegionShared.Backend.Events.Business.BusinessExpenseBalanceDeposit, {
      businessId,
      amount,
      paymentType,
    });
  }

  // Получить список улчшений от прошлого владельца
  public static async getFrozenImprovements(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.GetBusinessFrozenImprovementsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessFrozenImprovementsRequest,
      typeof RegionShared.Backend.Responses.GetBusinessFrozenImprovementsResponse
    >(RegionShared.Backend.Events.Business.GetFrozenImprovements, {
      businessId,
    });
  }

  // Принять улчшения от прошлого владельца
  public static async acceptFrozenImprovements(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.AcceptBusinessFrozenImprovementsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.AcceptBusinessFrozenImprovementsRequest,
      typeof RegionShared.Backend.Responses.AcceptBusinessFrozenImprovementsResponse
    >(RegionShared.Backend.Events.Business.AcceptFrozenImprovements, {
      businessId,
    });
  }

  // Отклонить улчшения от прошлого владельца
  public static async declineFrozenImprovements(
    businessId: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.DeclineBusinessFrozenImprovementsResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.DeclineBusinessFrozenImprovementsRequest,
      typeof RegionShared.Backend.Responses.DeclineBusinessFrozenImprovementsResponse
    >(RegionShared.Backend.Events.Business.DeclineFrozenImprovements, {
      businessId,
    });
  }

  // Получить логи
  public static async getHistory(
    businessId: number,
    page: number,
    pageSize: number,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.GetBusinessOrdersResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetBusinessOrdersRequest,
      typeof RegionShared.Backend.Responses.GetBusinessOrdersResponse
    >(RegionShared.Backend.Events.Business.GetBusinessOrders, {
      businessId,
      page,
      pageSize,
    });
  }
}
